import { useState } from "react"

// ** Third Party Components
import axios from "axios"
import { Check } from "lucide-react"
import { parseCookies } from "nookies"
import { useSearchParams } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

// ** Theme Components
import { Button } from "../common/button"
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from "../common/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../common/command"

// ** Helpers & Routes
import { DictType } from "@pma/types"
import { cn } from "@pma/utils/helpers"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import {
  FINANCIAL_SERVER_URL,
  GET_CONTRACTOR_COMPANY_LIST,
  SET_CONTRACTOR_COMPANY_PROJECT_ROLE,
} from "@pma/utils/api-routes"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** Custom hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

interface ProjectContactorCompanyProps {
  project: any
  refetch: CallableFunction
}

const ProjectContactorCompany = ({ project, refetch }: ProjectContactorCompanyProps) => {
  const { authConfig } = useAuth()
  const queryClient = useQueryClient()
  const [searchParam] = useSearchParams()
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const newParams = new URLSearchParams(searchParam)
  const organization_id = authConfig?.last_organization_id

  const [open, setOpen] = useState(false)

  const getContractorCompanyListApi = async () => {
    const response = await axios.get(FINANCIAL_SERVER_URL + GET_CONTRACTOR_COMPANY_LIST, {
      params: { organization_id: organization_id },
      headers: { Authorization: `Bearer ${accessToken}` },
    })

    return response.data
  }

  const { data: companies = [] } = useQuery({
    queryKey: ["contractor-companies"],
    queryFn: getContractorCompanyListApi,
  })

  const updateProjectDetailsApi = async (companyId: number) => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + SET_CONTRACTOR_COMPANY_PROJECT_ROLE,
      { project_id: project.id, company_id: companyId },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const updateProjectContractorCompanyMutation = useMutation({
    mutationKey: ["update-project-contractor", project?.id],
    mutationFn: updateProjectDetailsApi,
    onSuccess: async (data) => {
      newParams.set("revalidate", "projects")
      await queryClient.invalidateQueries({ queryKey: ["user-list"] })
      refetch("project")
    },
    onError: (error) => {
      toast({
        title: translate("Error"),
        description: translate("Error occurred while updating"),
        variant: "error",
      })
    },
  })

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={updateProjectContractorCompanyMutation.isPending}>
        <Button
          variant='outline'
          role='combobox'
          aria-expanded={open}
          className='flex h-8 w-full justify-start text-left capitalize text-black'
        >
          {project?._contractor_company
            ? project?._contractor_company?.name
            : translate("Select Contractor")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[250px] p-0'>
        <Command>
          <CommandInput placeholder={translate("Search for contractors") + "..."} />
          <CommandList>
            <CommandEmpty>{translate("No contractors found")}.</CommandEmpty>
            <CommandGroup className={isHebrew ? "rtl" : "ltr"}>
              {companies?.map((company: DictType) => (
                <PopoverClose className='w-full' key={company?.id}>
                  <CommandItem
                    value={company.id}
                    onSelect={() => updateProjectContractorCompanyMutation.mutate(company?.id)}
                    className={cn(
                      "my-1 flex cursor-pointer justify-between capitalize",
                      project?._contractor_company?.id === company?.id
                        ? "bg-secondary/30 text-black"
                        : "",
                    )}
                  >
                    {company?.name}
                    {project?._contractor_company?.id === company?.id ? (
                      <span>
                        <Check className='h-4 w-4 text-primary' />
                      </span>
                    ) : null}
                    <span></span>
                  </CommandItem>
                </PopoverClose>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default ProjectContactorCompany
